import React, { useId } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { commonStyles } from "../commonStyles";
import {
  DesignDialogButton,
  DesignDialogGlyph,
  type DesignDialogButtonProps,
} from "./DesignDialog";
import { DesignModalShell, designModalMessageStyle } from "./DesignModalShell";

// Shared confirm / destructive / success dialog. Confirm and destructive
// dialogs render the Claude Design standard (see the default branch below)
// through DesignDialog. The "emailSuccess" and "adminChamberSuccess"
// appearances keep their own approved shells.

export type ConfirmDialogVariant = "destructive" | "confirm" | "success";

export type ConfirmDialogConfirmColor = "red" | "blue" | "gold" | "green";

type ConfirmDialogBaseProps = {
  open: boolean;
  variant: ConfirmDialogVariant;
  /** Optional supporting paragraph under the title */
  message?: React.ReactNode;
  /**
   * Icon override; defaults: destructive/confirm -> QuestionMarkOutlinedIcon,
   * success -> CheckCircleOutlineIcon. Destructive delete flows pass
   * DeleteOutlineIcon.
   */
  icon?: React.ReactNode;
  /** Legacy size hint. Both sizes now render the Claude Design standard 440px confirm. */
  size?: "sm" | "lg";
  /** Legacy; the standard confirm always fits narrow screens. */
  constrainContentWidth?: boolean;
  /** default "Proceed"; ignored for success */
  confirmLabel?: string;
  /** default "Cancel"; ignored for success */
  cancelLabel?: string;
  /** Hides the secondary Cancel action when the donor dialog closes by X only. */
  hideCancel?: boolean;
  /** default false. TRUE renders PROCEED/CANCEL uppercase — uppercase ONLY via this prop */
  uppercaseActions?: boolean;
  /** defaults: destructive -> "red", confirm -> "blue" */
  confirmColor?: ConfirmDialogConfirmColor;
  /** Disables confirm, Cancel, and close; spinner in confirm button (CircularProgress size 16, color inherit) */
  loading?: boolean;
  /** e.g. 2000 to sit above fullscreen dialogs (applied to Dialog sx) */
  zIndex?: number;
  /** Required unless variant="success" */
  onConfirm?: () => void;
  /** Red circular X, backdrop, and Cancel all call this */
  onClose: () => void;
};

export type ConfirmDialogProps = ConfirmDialogBaseProps &
  (
    | {
        /** Additive compact appearance for email success messages. */
        appearance: "emailSuccess";
        variant: "success";
        /** Optional compact heading; omit for a message-only success. */
        title?: string;
        /** Accessible name when the visible heading is omitted. */
        ariaLabel?: string;
      }
    | {
        appearance?: "default" | "adminChamberSuccess";
        /** Optional 14px heading; the design's standard delete confirm is message-only. */
        title?: string;
        ariaLabel?: string;
      }
  );

const confirmTones: Record<
  ConfirmDialogConfirmColor,
  { hex: string; button: NonNullable<DesignDialogButtonProps["tone"]> }
> = {
  red: { hex: "#F21B1B", button: "red" },
  blue: { hex: "#2196F3", button: "blue" },
  gold: { hex: commonStyles.cpaGold, button: "gold" },
  green: { hex: "#10B981", button: "green" },
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  variant,
  title,
  message,
  icon,
  appearance = "default",
  ariaLabel,
  confirmLabel = "Proceed",
  cancelLabel = "Cancel",
  hideCancel = false,
  uppercaseActions = false,
  confirmColor,
  loading = false,
  zIndex,
  onConfirm,
  onClose,
}) => {
  const isSuccess = variant === "success";
  const accessibleId = useId();
  const titleId = `confirm-dialog-title-${accessibleId}`;
  const messageId = `confirm-dialog-message-${accessibleId}`;
  const resolvedConfirmColor: ConfirmDialogConfirmColor =
    confirmColor ?? (variant === "destructive" ? "red" : "blue");
  const dialogSx = zIndex === undefined ? undefined : { zIndex };
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };


  if (isSuccess && appearance === "emailSuccess") {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={message ? messageId : undefined}
        sx={{
          zIndex: zIndex ?? 4000,
          "& .MuiDialog-container": {
            boxSizing: "border-box",
            overflowY: "auto",
            padding: "clamp(12px, 4vw, 24px)",
          },
        }}
        BackdropProps={{
          sx: { backgroundColor: "rgba(10, 10, 10, 0.45)" },
        }}
        PaperProps={{
          "aria-label": title ? undefined : ariaLabel || "Success",
          "aria-labelledby": title ? titleId : undefined,
          sx: {
            alignItems: "center",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "14px",
            boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            fontFamily: "'DM Sans', sans-serif",
            margin: 0,
            maxHeight: "100%",
            maxWidth: "440px",
            padding: "30px 28px",
            position: "relative",
            width: "100%",
          },
        }}
      >
        <IconButton
          aria-label="Close"
          disabled={loading}
          onClick={handleClose}
          sx={{
            backgroundColor: "#F21B1B",
            color: "#FFFFFF",
            height: 28,
            padding: 0,
            position: "absolute",
            right: 14,
            top: 14,
            width: 28,
            "&:hover": { backgroundColor: "#D90F1A" },
          }}
        >
          <CloseIcon sx={{ fontSize: 13 }} />
        </IconButton>
        <div
          style={{
            alignItems: "center",
            backgroundColor: "#12B981",
            borderRadius: "50%",
            color: "#FFFFFF",
            display: "flex",
            flexShrink: 0,
            height: 64,
            justifyContent: "center",
            width: 64,
          }}
        >
          {icon ?? <CheckCircleOutlineIcon sx={{ fontSize: 37.12 }} />}
        </div>
        {title ? (
          <div
            id={titleId}
            style={{
              color: "#0A0A0A",
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.55,
              marginTop: 18,
              maxWidth: 340,
              textAlign: "center",
            }}
          >
            {title}
          </div>
        ) : null}
        {message ? (
          <DialogContent
            id={messageId}
            sx={{
              color: "#0A0A0A",
              flex: "0 1 auto",
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: 400,
              lineHeight: 1.55,
              marginTop: title ? "8px" : "18px",
              maxWidth: "340px",
              overflowWrap: "anywhere",
              padding: 0,
              textAlign: "center",
              textWrap: "pretty",
              "& strong, & b": { fontWeight: 600 },
            }}
          >
            {message}
          </DialogContent>
        ) : null}
      </Dialog>
    );
  }

  if (isSuccess && appearance === "adminChamberSuccess") {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        sx={dialogSx}
        aria-labelledby={titleId}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            maxWidth: "505px",
            overflow: "visible",
            width: "100%",
          },
        }}
      >
        <div className="relative px-[18px] py-6 text-center min-[601px]:p-8">
          <IconButton
            aria-label="close"
            disabled={loading}
            onClick={handleClose}
            sx={{
              backgroundColor: "#ED1C24",
              boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
              color: "white",
              height: 30,
              padding: 0,
              position: "absolute",
              right: 8,
              top: 8,
              width: 30,
              "&:hover": { backgroundColor: "#D41920" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <div className="mx-auto mb-4 flex h-[105px] w-[105px] items-center justify-center rounded-full border border-white bg-[#00B621]">
            <svg
              width="60"
              height="60"
              viewBox="0 0 60 60"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="30"
                cy="30"
                r="25"
                stroke="white"
                strokeWidth="4"
              />
              <path
                d="M18 30L26 38L42 22"
                stroke="white"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p
            id={titleId}
            className="m-0 w-full text-center text-lg font-semibold leading-[2] text-black"
          >
            {title}
          </p>
        </div>
      </Dialog>
    );
  }

  // Claude Design standard confirm (CMP Dashboard.dc.html delConfirm and
  // ds-components/InvoiceConfirmModal): 440px card, 64px circle, 14px title
  // and message, buttons up to 170px. Both legacy sizes render this shell so
  // every consumer matches.
  const tone = confirmTones[resolvedConfirmColor];
  const circleColor = isSuccess ? "#12B981" : tone.hex;
  const defaultIconName = isSuccess ? "success" : variant === "destructive" ? "trash" : "question";
  const actionTextTransform = uppercaseActions ? "uppercase" : "none";
  return (
    <DesignModalShell
      open={open}
      onClose={onClose}
      busy={loading}
      circleColor={circleColor}
      icon={icon ?? <DesignDialogGlyph icon={defaultIconName} />}
      labelledBy={title ? titleId : undefined}
      describedBy={message ? messageId : undefined}
      ariaLabel={ariaLabel}
      zIndex={zIndex}
    >
      {title ? (
        <h2
          id={titleId}
          style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, margin: "18px 0 0" }}
        >
          {title}
        </h2>
      ) : null}
      {message ? (
        <div
          id={messageId}
          className="[&_b]:font-semibold [&_strong]:font-semibold"
          style={{ ...designModalMessageStyle, marginTop: title ? 8 : 18 }}
        >
          {message}
        </div>
      ) : null}
      {isSuccess ? null : (
        <div className="mt-[22px] flex w-full justify-center gap-[12px] [&>button]:max-w-[170px]">
          <DesignDialogButton
            tone={tone.button}
            disabled={loading}
            onClick={onConfirm}
            style={{ textTransform: actionTextTransform }}
          >
            {loading ? (
              <CircularProgress color="inherit" size={16} />
            ) : variant === "destructive" ? (
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            ) : null}
            {confirmLabel}
          </DesignDialogButton>
          {hideCancel ? null : (
            <DesignDialogButton
              tone="dark"
              disabled={loading}
              onClick={handleClose}
              style={{ textTransform: actionTextTransform }}
            >
              {cancelLabel}
            </DesignDialogButton>
          )}
        </div>
      )}
    </DesignModalShell>
  );
};
